import { useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import { Close as CloseIcon, Info as InfoIcon, History as HistoryIcon, Comment as CommentIcon, Person as PersonIcon, Event as EventIcon, Folder as FolderIcon, Description as DescriptionIcon } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
function TabPanel(props) {
  const {
    children,
    value,
    index,
    ...other
  } = props;
  return <div role="tabpanel" hidden={value !== index} id={`viewer-tabpanel-${index}`} aria-labelledby={`viewer-tab-${index}`} {...other} style={{
    height: '100%'
  }}>
      {value === index && <Box sx={{
      height: '100%'
    }}>{children}</Box>}
    </div>;
}
export function ViewerInfoDrawer({
  open,
  onClose,
  fileName,
  fileDetails = {},
  activities = [],
  comments = [],
  container = null,
  zIndex = null,
  hideBackdrop = false,
  isLoadingActivities = false
}) {
  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };
  const {
    owner = 'Unknown',
    dateModified = 'N/A',
    dateCreated = 'N/A',
    fileType = 'PDF Document',
    size = 'N/A',
    description = '',
    location = 'My Documents'
  } = fileDetails;
  const drawerZIndex = zIndex !== null ? zIndex : (container ? 1300 : undefined);
  
  // Handle close - prevent closing only on escape key when hideBackdrop is true
  // Allow backdrop clicks to close the drawer
  const handleClose = (event, reason) => {
    if (hideBackdrop && reason === 'escapeKeyDown') {
      return; // Don't close on escape key when hideBackdrop is true
    }
    onClose(event, reason);
  };
  
  return <Drawer 
    anchor="right" 
    open={open} 
    onClose={handleClose}
    container={container}
    sx={{
      zIndex: drawerZIndex,
      '& .MuiBackdrop-root': {
        backgroundColor: hideBackdrop ? 'transparent !important' : 'rgba(0, 0, 0, 0.3)',
        opacity: hideBackdrop ? 0 : 1,
        // Keep pointer events enabled so backdrop clicks work
        pointerEvents: 'auto'
      },
      // Prevent clicks inside drawer content from closing it
      '& .MuiDrawer-paper': {
        pointerEvents: 'auto',
        zIndex: drawerZIndex ? drawerZIndex + 1 : undefined,
        right: 0,
        left: 'auto !important'
      }
    }}
    ModalProps={{
      style: {
        zIndex: drawerZIndex
      },
      BackdropProps: {
        style: {
          zIndex: drawerZIndex ? drawerZIndex - 1 : undefined,
          backgroundColor: hideBackdrop ? 'transparent' : 'rgba(0, 0, 0, 0.3)',
          opacity: hideBackdrop ? 0 : 1,
          // Keep pointer events enabled so backdrop clicks work
          pointerEvents: 'auto'
        },
        onClick: (e) => {
          // Only close if clicking directly on backdrop, not on drawer content
          if (e.target === e.currentTarget) {
            handleClose(e, 'backdropClick');
          }
        }
      },
      disableEscapeKeyDown: hideBackdrop
    }}
    PaperProps={{
      sx: {
        width: {
          xs: '100%',
          sm: '400px'
        },
        bgcolor: '#FFFFFF',
        zIndex: drawerZIndex ? drawerZIndex + 1 : undefined,
        right: 0,
        left: 'auto'
      },
      onClick: (e) => {
        // Prevent clicks inside drawer from closing it
        e.stopPropagation();
      }
    }}
  >
      {/* Drawer Header */}
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid #E5E7EB',
          bgcolor: '#F2F2F2'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography sx={{
        fontFamily: 'Roboto, sans-serif',
        fontSize: '16px',
        fontWeight: 500,
        color: '#212121'
      }}>
          File Information
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{
        color: '#4A5565'
      }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Box sx={{
      borderBottom: '1px solid #E5E7EB'
    }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{
        minHeight: '48px',
        '& .MuiTab-root': {
          minHeight: '48px',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '13px',
          textTransform: 'none',
          color: '#4A5565',
          '&.Mui-selected': {
            color: '#2932E5'
          }
        },
        '& .MuiTabs-indicator': {
          backgroundColor: '#2932E5'
        }
      }}>
          <Tab icon={<InfoIcon fontSize="small" />} iconPosition="start" label="Details" />
          <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="Activity" />
          <Tab icon={<CommentIcon fontSize="small" />} iconPosition="start" label="Comments" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box 
        sx={{
          flexGrow: 1,
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Details Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{
          p: 2
        }}>
            {/* File Name */}
            <Box sx={{
            mb: 3
          }}>
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1
            }}>
                <DescriptionIcon sx={{
                fontSize: '20px',
                color: '#FF8555'
              }} />
                <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                fontWeight: 600,
                color: '#4A5565',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                  File Name
                </Typography>
              </Box>
              <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              color: '#212121',
              wordBreak: 'break-word'
            }}>
                {fileName}
              </Typography>
            </Box>

            <Divider sx={{
            my: 2
          }} />

            {/* Owner */}
            <Box sx={{
            mb: 2
          }}>
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.5
            }}>
                <PersonIcon sx={{
                fontSize: '18px',
                color: '#4A5565'
              }} />
                <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                color: '#4A5565'
              }}>
                  Owner
                </Typography>
              </Box>
              <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              color: '#212121',
              ml: 3.5
            }}>
                {owner}
              </Typography>
            </Box>

            {/* Location */}
            <Box sx={{
            mb: 2
          }}>
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.5
            }}>
                <FolderIcon sx={{
                fontSize: '18px',
                color: '#4A5565'
              }} />
                <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                color: '#4A5565'
              }}>
                  Location
                </Typography>
              </Box>
              <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              color: '#212121',
              ml: 3.5
            }}>
                {location}
              </Typography>
            </Box>

            {/* Date Modified */}
            <Box sx={{
            mb: 2
          }}>
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.5
            }}>
                <EventIcon sx={{
                fontSize: '18px',
                color: '#4A5565'
              }} />
                <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                color: '#4A5565'
              }}>
                  Modified
                </Typography>
              </Box>
              <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              color: '#212121',
              ml: 3.5
            }}>
                {dateModified}
              </Typography>
            </Box>

            {/* Date Created */}
            <Box sx={{
            mb: 2
          }}>
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.5
            }}>
                <EventIcon sx={{
                fontSize: '18px',
                color: '#4A5565'
              }} />
                <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                color: '#4A5565'
              }}>
                  Created
                </Typography>
              </Box>
              <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              color: '#212121',
              ml: 3.5
            }}>
                {dateCreated}
              </Typography>
            </Box>

            <Divider sx={{
            my: 2
          }} />

            {/* File Type */}
            <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}>
              <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              color: '#4A5565'
            }}>
                Type
              </Typography>
              <Chip label={fileType} size="small" sx={{
              bgcolor: '#FFE0D0',
              color: '#FF8555',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '12px',
              fontWeight: 500
            }} />
            </Box>

            {/* Size */}
            <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}>
              <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              color: '#4A5565'
            }}>
                Size
              </Typography>
              <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              color: '#212121'
            }}>
                {size}
              </Typography>
            </Box>

            {/* Description */}
            {description && <>
                <Divider sx={{
              my: 2
            }} />
                <Box>
                  <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                fontWeight: 600,
                color: '#4A5565',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1
              }}>
                    Description
                  </Typography>
                  <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                color: '#212121',
                lineHeight: 1.6
              }}>
                    {description}
                  </Typography>
                </Box>
              </>}
          </Box>
        </TabPanel>

        {/* Activity Tab */}
        <TabPanel value={activeTab} index={1}>
          {isLoadingActivities ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4
            }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List sx={{
              p: 0
            }}>
              {activities.length > 0 ? activities.map((activity, index) => {
                // Format activity data - handle different API response structures
                const activityType = activity.activity_type || activity.action || activity.type || "Activity";
                // Capitalize first letter and format activity type
                const activityAction = activityType.charAt(0).toUpperCase() + activityType.slice(1);
                const activityUser = activity.user_name || activity.username || activity.user || activity.created_by || "Unknown";
                // Format timestamp
                const formatTimestamp = (timestamp) => {
                  if (!timestamp) return "N/A";
                  try {
                    const date = new Date(timestamp);
                    return date.toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  } catch (e) {
                    return timestamp;
                  }
                };
                const activityTimestamp = formatTimestamp(activity.created_at || activity.timestamp || activity.date || activity.time);
                // Handle details - it can be an object or string
                const getActivityDetails = () => {
                  if (!activity.details) return null;
                  if (typeof activity.details === 'string') {
                    return activity.details;
                  }
                  if (typeof activity.details === 'object') {
                    // Format details object
                    const parts = [];
                    if (activity.details.name) parts.push(`Name: ${activity.details.name}`);
                    if (activity.details.type) parts.push(`Type: ${activity.details.type}`);
                    if (activity.details.parent_id) parts.push(`Parent ID: ${activity.details.parent_id}`);
                    return parts.length > 0 ? parts.join(', ') : null;
                  }
                  return activity.description || activity.message || null;
                };
                const activityDetails = getActivityDetails();
                const activityId = activity.id || activity._id || index;

                return (
                  <Box key={activityId}>
                    <ListItem sx={{
                      py: 2,
                      px: 2,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}>
                      <Box sx={{
                        display: "flex",
                        alignItems: "start",
                        gap: 2,
                        width: "100%",
                      }}>
                        <Avatar sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "#2932E5",
                          fontSize: "14px",
                        }}>
                          {activityUser.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{
                          flex: 1,
                        }}>
                          <Typography sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#212121',
                            mb: 0.5
                          }}>
                            {activityAction}
                          </Typography>
                          <Typography component="span" sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '13px',
                            color: '#4A5565',
                            display: 'block'
                          }}>
                            {activityUser} • {activityTimestamp}
                          </Typography>
                          {activityDetails && (
                            <Typography component="span" sx={{
                              fontFamily: 'Roboto, sans-serif',
                              fontSize: '12px',
                              color: '#4A5565',
                              display: 'block',
                              mt: 0.5
                            }}>
                              {activityDetails}
                            </Typography>
                          )}
                        </Box>
                        <HistoryIcon sx={{
                          fontSize: 20,
                          color: '#5F6368',
                        }} />
                      </Box>
                    </ListItem>
                    {index < activities.length - 1 && <Divider />}
                  </Box>
                );
              }) : (
                <Box sx={{
                  p: 3,
                  textAlign: 'center'
                }}>
                  <HistoryIcon sx={{
                    fontSize: 48,
                    color: '#E5E7EB',
                    mb: 1
                  }} />
                  <Typography sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: '#4A5565'
                  }}>
                    No activity yet
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </TabPanel>

        {/* Comments Tab */}
        <TabPanel value={activeTab} index={2}>
          <List sx={{
          p: 0
        }}>
            {comments.length > 0 ? comments.map((comment, index) => <Box key={comment.id}>
                  <ListItem sx={{
              py: 2,
              px: 2,
              alignItems: 'flex-start'
            }}>
                    <ListItemText primary={<Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '13px',
                fontWeight: 500,
                color: '#212121',
                mb: 0.5
              }}>
                          {comment.user}
                        </Typography>} secondary={<>
                          <Typography component="span" sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '12px',
                  color: '#4A5565',
                  display: 'block',
                  mb: 1
                }}>
                            {comment.timestamp}
                          </Typography>
                          <Typography component="span" sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#212121',
                  display: 'block',
                  lineHeight: 1.6
                }}>
                            {comment.text}
                          </Typography>
                        </>} />
                  </ListItem>
                  {index < comments.length - 1 && <Divider />}
                </Box>) : <Box sx={{
            p: 3,
            textAlign: 'center'
          }}>
                <CommentIcon sx={{
              fontSize: 48,
              color: '#E5E7EB',
              mb: 1
            }} />
                <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              color: '#4A5565'
            }}>
                  No comments yet
                </Typography>
              </Box>}
          </List>
        </TabPanel>
      </Box>
    </Drawer>;
}